import type { UsersApiService } from './UsersApiService'
import type { IAppLifecycleService } from '../core/lifecycle/AppLifecycleService'
import { AuthService } from '../core/auth/services/AuthService.ts'
import { logError } from '../common/errors/log-utils'
import type { UserModel } from './UserModel'
import { BehaviorSubject } from 'rxjs'
import type { IBehaviorObservable } from '../common/react/IBehaviorObservable'
import { localStorageKeys } from '../core/configs/local-storage-keys'
import { StringLocalStorageValue } from '../common/local-storage/StringLocalStorageValue'

export class UsersService {
  public readonly user$: IBehaviorObservable<UserModel | undefined>
  private readonly userSubject: BehaviorSubject<UserModel | undefined>
  private readonly userName = new StringLocalStorageValue(localStorageKeys.UserName)

  constructor(
    private readonly userApiService: UsersApiService,
    private readonly appLifecycleService: IAppLifecycleService,
    private readonly authService: AuthService,
  ) {
    this.userSubject = new BehaviorSubject<UserModel | undefined>(undefined)
    this.user$ = this.userSubject

    this.appLifecycleService.onInit$.subscribe(this.onAppInit.bind(this))
    this.authService.onAuthStatusChange$.subscribe(this.onAuthChanged.bind(this))
  }

  private onAppInit(): void {
    if (!this.authService.isLoggedIn()) {
      return
    }

    const name = this.userName.value
    if (name) {
      this.userSubject.next({ name } as UserModel)
    }

    void this.fetchAndEmitUser()
  }

  private onAuthChanged(isAuthed: boolean) {
    if (!isAuthed) {
      return
    }

    void this.fetchAndEmitUser()
  }

  private async fetchAndEmitUser(): Promise<void> {
    const { result: user, error } = await this.userApiService.getUser()
    if (error) {
      logError('error fetching user data')
      return
    }

    this.userSubject.next(user)
    this.userName.value = user!.name
  }
}
